import { useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import useProviderConfig from 'hooks/useProviderConfig';
import {
  couponColumns,
  couponOrderColumns,
  formatMoney,
  getCouponCellValue,
} from './couponReportColumns';

const rowsPerPageOptions = [5, 10, 20, 50, 100];

const TotalsFooter = ({ row }) => (
  <TableFooter>
    <TableRow>
      {couponOrderColumns.map((column) => (
        <TableCell
          key={column.field}
          align={column.numeric ? 'right' : 'left'}
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'text.primary',
            borderBottom: 'none',
          }}
        >
          {column.totalField ? formatMoney(row[column.totalField]) : ''}
        </TableCell>
      ))}
    </TableRow>
  </TableFooter>
);

const CouponRow = ({ row }) => {
  const { t } = useTranslation();
  const { providers } = useProviderConfig();
  const [open, setOpen] = useState(false);
  const orders = row.orders || [];

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}
      >
        <TableCell sx={{ width: '48px' }}>
          <IconButton
            size="small"
            aria-label={t('reports_settings.show_coupon_orders')}
            aria-expanded={open}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {couponColumns.map((column) => (
          <TableCell
            key={column.field}
            align={column.numeric ? 'right' : 'left'}
            sx={{ fontSize: '14px' }}
          >
            {getCouponCellValue(row, column, { t, providers })}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell
          sx={{ py: 0, borderBottom: open ? undefined : 'none' }}
          colSpan={couponColumns.length + 1}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '16px', mb: 1 }}>
                {t('reports_settings.coupon_orders')}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {couponOrderColumns.map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.numeric ? 'right' : 'left'}
                        sx={{ fontWeight: 600 }}
                      >
                        {t(column.label)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order, index) => (
                    <TableRow key={`${order.order_number}-${index}`}>
                      {couponOrderColumns.map((column) => (
                        <TableCell
                          key={column.field}
                          align={column.numeric ? 'right' : 'left'}
                        >
                          {getCouponCellValue(order, column, { t, providers })}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
                <TotalsFooter row={row} />
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CouponReportTable = ({ reportList }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  useEffect(() => {
    setPage(0);
  }, [reportList]);

  const visibleRows = reportList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ my: 2 }}>
      <TableContainer>
        <Table>
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              '& .MuiTableCell-head': { color: 'white', fontWeight: 600 },
            }}
          >
            <TableRow>
              <TableCell sx={{ width: '48px' }} />
              {couponColumns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.numeric ? 'right' : 'left'}
                >
                  {t(column.label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <CouponRow key={`${row.coupon_id}-${row.coupon_code}`} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={reportList.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        sx={{
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
            {
              margin: 0,
            },
        }}
      />
      <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 1 }}>
        {t('reports_settings.coupon_report_overlap_note')}
      </Typography>
    </Box>
  );
};

export default CouponReportTable;
